// Web CRM (Business plane) — Scheduling manager.
//
// Real-backend ONLY (scheduling backend candidate). NO fake rows, NO local-only bookings, NO
// success toast before the backend confirms, NO client-inferred permissions.
//   list    : GET  /api/v1/scheduling/appointments        (+ per-row server allowed_actions)
//   detail  : GET  /api/v1/scheduling/appointments/{id}   (+ policy + allowed_actions + thread)
//   actions : POST /api/v1/scheduling/appointments/{id}/confirm|decline|cancel|reschedule
//   slots   : GET  /api/v1/scheduling/availability        (real slots for reschedule — never invented)
//   thread  : GET  /api/v1/conversations/{conversation_id}/messages  (existing thread surface;
//             appointment_*_event rows are written to db.messages by the backend and render here)
//
// Every action button is rendered from the server-computed `allowed_actions` on the row/detail.
// The tenant is scoped by currentBusiness.brandId (backend also enforces tenant membership).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../hooks/use-toast';
import { auth } from '../../../lib/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { formatInstantWithZone } from '../../../lib/datetime';
import { getErrorMessage } from '../../../lib/errorMessage';
import MoversJobPanel, { useServiceWorkflowEligibility } from './MoversJobPanel';
import DocumentPanel from './DocumentPanel';
import DraftPanel from './DraftPanel';
import PersonalCalendarSection from './PersonalCalendarSection';
import AppointmentCaptureControl from './AppointmentCaptureControl';
import { createCaptureApi } from '../../../lib/encounterCaptureApi';
import {
  Calendar, CalendarClock, Loader2, RefreshCw, CheckCircle2, XCircle, Ban,
  User, ClipboardList, ShieldCheck, MessageSquare, Plus,
} from 'lucide-react';
import { tenantHeaders } from '../../../lib/tenantSession';
// P7 — the ONE Business Web call authority. Read through the context, never by calling useWebRTC()
// again: a second instance opens a competing signaling socket (the sessions_for_user>1 defect the
// provider exists to prevent). useContext (not useWebRTCContext) because a null provider must make
// the Join action ABSENT, not throw a page-level error over a booking list that is otherwise fine.
import { useContext } from 'react';
import { WebRTCContext } from '../../WebRTC/WebRTCContext';

// The backend's own default page. Kept in step with DEFAULT_LIST_LIMIT in api/scheduling_routes.py
// and with the mobile Business planes, so all three page identically against one contract.
const PAGE_SIZE = 100;

/** A picked calendar day -> the first instant of that day in the VIEWER's timezone, as UTC.
 *  `new Date('2026-09-10')` is parsed as UTC midnight; appending a time makes it local. */
function dayStartIso(day) {
  if (!day) return '';
  const d = new Date(`${String(day).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/** ...and the LAST instant of that day, so a To date includes the day itself rather than stopping
 *  at its first second. The backend range is inclusive at both ends. */
function dayEndIso(day) {
  if (!day) return '';
  const d = new Date(`${String(day).slice(0, 10)}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

const STATUS_OPTIONS = [
  'pending', 'requested', 'reschedule_requested', 'confirmed', 'rescheduled',
  'completed', 'declined', 'cancelled', 'no_show', 'expired', 'failed',
];

// Badge colour per status (static chrome; the label itself is localized via i18n).
const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-800',
  requested: 'bg-amber-100 text-amber-800',
  reschedule_requested: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-green-100 text-green-800',
  rescheduled: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-700',
  no_show: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-700',
  failed: 'bg-red-100 text-red-800',
};

// GOV-7 — customer-typeahead disambiguator SAFETY. A search result's canonical id (customer_id/user id)
// must NEVER be rendered as a human-facing label; the secondary line may show ONLY a plausible phone or
// email. Generic + tenant-agnostic: it validates the SHAPE of the value, so a record whose `phone` field
// wrongly holds an internal identifier (e.g. "user-…", a UID/ObjectId/UUID, or a bare "cust_…") is shown as
// name-only rather than leaking the identifier. Precedence: real phone → real email → nothing. The canonical
// id stays available to the caller for selection/state/payload (never routed through here).
const _looksLikeEmail = (s) => typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
const _looksLikePhone = (s) => {
  if (typeof s !== 'string') return false;
  const t = s.trim();
  if (!/^\+?[\d][\d()\-.\s]{4,}$/.test(t)) return false;      // starts with a digit/+, phone-ish punctuation only
  return (t.replace(/\D/g, '').length) >= 5;                   // enough real digits to be a phone, not an id
};
export const safeContactLabel = (c) => {
  const phone = (c && c.phone) || '';
  if (_looksLikePhone(phone)) return String(phone).trim();
  const email = (c && c.email) || '';
  if (_looksLikeEmail(email)) return String(email).trim();
  return null;                                                 // never fall back to any identifier
};

// GOV-7 §19 — GENERIC availability-slot resource-name display rule, driven by the effective profile's
// provider_resource_hint (surfaced read-only on effective_config; NO engine/eligibility change). Person/staff
// resources (Clinic "staff") keep the individual provider/person name on slots; NON-person resources (Movers
// "equipment", Hotel "room") show TIMES ONLY — provider_staff_id stays the INTERNAL calendar anchor, never a
// customer/staff-facing label. Conservative default: an empty/absent/unknown hint preserves the legacy
// name-visible behaviour (also safe during a canary where an older backend omits the field). No profile-name,
// industry, or provider-name branching anywhere.
export const shouldShowProviderName = (resourceHint) => {
  const h = (resourceHint == null ? '' : String(resourceHint)).trim().toLowerCase();
  return h === '' || h === 'staff';
};

//: How long a user action will wait for a refreshed token before falling back.
export const TOKEN_WAIT_MS = 8000;

/**
 * A bearer token for this operator, or '' if none can be obtained.
 *
 * `getIdToken()` can hang indefinitely when Firebase's refresh is wedged -- an aged session, a
 * stalled refresh -- and in that state it never rejects, so nothing downstream ever runs. That is
 * exactly how Accept failed in staging: the click landed, `setBusy` disabled the button, the await
 * below never returned, `fetch` was never called, the catch was never entered, and `finally` never
 * cleared busy. No request, no error, a permanently spinning button and no way to tell why.
 *
 * So the wait is bounded. On timeout we fall through to the stored token -- which is what this
 * function already does whenever Firebase cannot produce one -- and if that is empty too the
 * request goes out unauthenticated and comes back 401, which the caller renders honestly. A refused
 * action a user can see beats a silent one they cannot.
 */
export const _getToken = async () => {
  try {
    if (auth.currentUser) {
      const fresh = await Promise.race([
        auth.currentUser.getIdToken(),
        new Promise((resolve) => setTimeout(() => resolve(null), TOKEN_WAIT_MS)),
      ]);
      if (fresh) return fresh;
    }
  } catch (e) { /* fall through */ }
  return localStorage.getItem('crm_access_token') || '';
};

// E5 — the module-level `const fmt = (ts) => new Date(ts).toLocaleString()` that used to live here
// rendered every appointment in the OPERATOR'S BROWSER ZONE. It never consulted a timezone at all, so
// a clinic in America/Edmonton showed the wrong wall clock to anyone whose laptop was set elsewhere,
// while the availability engine went on scheduling in tenant time. The formatter is now built inside
// the component from `SchedulingConfig.timezone` — the SCHEDULING authority, not `business.timezone`,
// because a tenant sets those two independently and they are wrong exactly when they disagree.

const SchedulingManager = () => {
  const { currentBusiness, getBusinessStaff } = useBusiness();
  const { t } = useLanguage();
  const { toast } = useToast();
  const brandId = currentBusiness?.brandId || '';
  const base = process.env.REACT_APP_BACKEND_URL || '';

  // M4 — the ONE Web capture transport, the same one Unified Desktop binds, over the same token
  // helper. Memoised so the capture control is not handed a new object on every keystroke in the
  // filter bar, which would re-bind its subscription for no reason.
  const captureApi = useMemo(() => createCaptureApi(_getToken), []);

  // GOV-7: the ONE Scheduling surface renders the generic field-service (Movers) job panel ONLY when the
  // BACKEND service-workflow eligibility gate says so — no industry-name / tenant-id / duplicate surface.
  // GOV-7 WS2-B: `_api` also carries put/post so the panel's assign/transition/eta mutations reuse the SAME
  // authenticated helper (Firebase token → crm_access_token fallback) as the rest of this surface — no new
  // api client, no direct-write bypass. Each method resolves { ok, status, data } for truthful handling.
  const _api = useMemo(() => ({
    get: async (url) => {
      const token = await _getToken();
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } });
      return { ok: r.ok, status: r.status, data: r.ok ? await r.json() : null };
    },
    put: async (url, body) => {
      const token = await _getToken();
      const r = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify(body || {}),
      });
      const data = await r.json().catch(() => null);
      return { ok: r.ok, status: r.status, data };
    },
    // E9 needs PATCH; the helper carried get/put/post only. Added here so the waitlist reuses the SAME
    // authenticated client (Firebase token -> crm_access_token fallback, tenant headers) as every other
    // mutation on this surface, rather than a second raw fetch that would bypass tenant headers.
    patch: async (url, body) => {
      const token = await _getToken();
      const r = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify(body || {}),
      });
      const data = await r.json().catch(() => null);
      return { ok: r.ok, status: r.status, data };
    },
    post: async (url, body) => {
      const token = await _getToken();
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify(body || {}),
      });
      const data = await r.json().catch(() => null);
      return { ok: r.ok, status: r.status, data };
    },
  }), []);
  const { eligibility: workflowEligibility } = useServiceWorkflowEligibility(_api);

  // GOV-7 WS2-B: crew NAME-selector reuses the CANONICAL tenant roster already loaded by BusinessContext
  // (businessStaff ← GET /api/v1/billing/staff/by-brand). No duplicate fetch, no new staff directory.
  const staffRoster = useMemo(() => {
    const list = (typeof getBusinessStaff === 'function' ? getBusinessStaff() : []) || [];
    return list.map((m) => ({ id: m.id, name: m.name || m.email || '', role: m.role || '' }));
  }, [getBusinessStaff]);

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', provider: '', customer: '', from: '', to: '' });
  // SERVER-BACKED PAGINATION. `offset` is sent to the backend; nothing is sliced in the browser.
  // Fetching everything and paging locally would reproduce the defect this fixes — the list is
  // ordered oldest-first and cut at the page size, so the rows that matter are the ones the browser
  // would never receive.
  const [offset, setOffset] = useState(0);
  const [pageInfo, setPageInfo] = useState({ total: 0, count: 0, truncated: false });
  // Bumped by Search and Clear so an explicit re-query refetches even when the offset is already 0.
  const [queryNonce, setQueryNonce] = useState(0);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  // P7 consultation entry. `joining` is local UI state only; the RIGHT to enter belongs to the server
  // and is re-decided on every click.
  const rtc = useContext(WebRTCContext);
  const [joining, setJoining] = useState(false);
  const [joinErr, setJoinErr] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [threadMsgs, setThreadMsgs] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  const [modal, setModal] = useState(null); // 'confirm' | 'decline' | 'cancel' | 'reschedule'
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState(false);
  const [acting, setActing] = useState(false);

  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [chosenSlot, setChosenSlot] = useState(null);

  // ---- create-booking ----
  const [createOpen, setCreateOpen] = useState(false);
  const [cfgTypes, setCfgTypes] = useState([]);      // effective (profile/pack) types when present, else config types
  const [cfgProviders, setCfgProviders] = useState([]);
  const [cfgTimezone, setCfgTimezone] = useState('');   // E5: SchedulingConfig.timezone, display only
  // E9 — the waitlist queue for this tenant. Lives on the EXISTING Scheduling surface: a queue is a
  // scheduling concern, and a separate page would be a second place to look for the same thing.
  // Loaded only when the capability is usable; a tenant without the add-on sees nothing at all.
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistAvailable, setWaitlistAvailable] = useState(false);
  const [waitlistBusy, setWaitlistBusy] = useState('');
  // GOV-7 Issue B — profile-driven terminology sourced from the effective_config the /config endpoint already
  // returns (customer_display_label / resource_display_label). Empty → fall back to the generic i18n labels.
  const [effResourceLabel, setEffResourceLabel] = useState('');
  const [effCustomerLabel, setEffCustomerLabel] = useState('');
  // §19 — effective resource nature (staff|equipment|room|…) for the slot-display rule only.
  const [effResourceHint, setEffResourceHint] = useState('');
  // GOV-7 — OPTIONAL crew pre-selection at manual booking for a non-person resource profile. Reuses the
  // canonical tenant staff roster (staffRoster) + the canonical crew authority (crew_staff_ids via the
  // existing PUT /appointments/{id}/assignment). NO new store, NO create-contract change: create-then-assign.
  const [createCrew, setCreateCrew] = useState([]);
  const [cForm, setCForm] = useState({ type: '', provider: '', customerId: '', phone: '', email: '', reason: '', pickup: '', destination: '' });
  // Customer typeahead (reuses GET /customers/business/{id}?search= — the ONE customer authority; no new store).
  const [custQuery, setCustQuery] = useState('');
  const [custResults, setCustResults] = useState(null);
  const [custSearching, setCustSearching] = useState(false);
  /** A SEARCH THAT FAILED IS NOT A SEARCH THAT FOUND NOBODY. Kept separate from `custResults` so a
   *  backend failure can never be rendered as "No matching customer" — which is exactly how the
   *  phone-search 500 presented to the operator: as though the patient did not exist. */
  const [custError, setCustError] = useState(false);
  /** Monotonic id of the newest search. Only the newest may touch the UI. */
  const custSeqRef = useRef(0);
  /** The in-flight search, so a newer keystroke can abort it rather than race it. */
  const custAbortRef = useRef(null);
  const [custSelected, setCustSelected] = useState(null);
  const [cSlots, setCSlots] = useState(null);
  const [cSlotsLoading, setCSlotsLoading] = useState(false);
  const [cChosen, setCChosen] = useState(null);
  const [creating, setCreating] = useState(false);

  // Effective booking-requirement gate — from the SAME backend eligibility the Movers job panel trusts
  // (booking_config.pickup_required / destination_required / location_consent_required + profile consent_required).
  // Gating display on these keeps non-field-service profiles (Clinic/base) identical (no pickup/destination shown).
  // E5 — one formatter for every appointment instant on this screen, rendered in the tenant's
  // SCHEDULING timezone and labelled with the zone in force AT that instant (MDT vs MST), so an
  // operator can always tell whose clock they are reading. Projection only: the canonical UTC
  // instant and SchedulingConfig.timezone are both untouched.
  const fmt = useCallback(
    (ts) => (ts ? (formatInstantWithZone(ts, cfgTimezone) || String(ts)) : '—'),
    [cfgTimezone],
  );

  // E9 — read the queue through the ONE waitlist authority. A 402/403 here is the capability gate
  // doing its job for a tenant without the add-on, NOT an error worth showing: the panel simply does
  // not render. Any other failure leaves the panel hidden too rather than asserting an empty queue.
  const loadWaitlist = useCallback(async () => {
    if (!brandId) return;
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const r = await _api.get(
        `${base}/api/v1/scheduling/waitlist?business_id=${encodeURIComponent(brandId)}&open_only=true`);
      if (!r.ok) { setWaitlistAvailable(false); setWaitlist([]); return; }
      setWaitlist(Array.isArray(r.data?.waitlist) ? r.data.waitlist : []);
      setWaitlistAvailable(true);
    } catch (e) {
      setWaitlistAvailable(false); setWaitlist([]);
    }
  }, [brandId, _api]);

  useEffect(() => { loadWaitlist(); }, [loadWaitlist]);

  // The ONLY state-changing waitlist action on this surface. It calls the canonical backend authority
  // and NEVER creates an appointment: a queue entry is not a booking, and promoting one to a booking
  // goes through the normal SchedulingEngine after the customer accepts.
  const setWaitlistStatus = async (entryId, status) => {
    if (waitlistBusy) return;
    setWaitlistBusy(entryId);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const r = await _api.patch(
        `${base}/api/v1/scheduling/waitlist/${encodeURIComponent(entryId)}/status`,
        { business_id: brandId, status });
      if (!r.ok) throw new Error('waitlist_status_failed');
      await loadWaitlist();
      toast({ title: t('sched.waitlist.updated') });
    } catch (e) {
      toast({ title: t('sched.waitlist.updateFailed'), variant: 'destructive' });
    } finally {
      setWaitlistBusy('');
    }
  };

  const bookingCfg = workflowEligibility?.booking_config || {};
  const pickupRequired = !!bookingCfg.pickup_required;
  const destinationRequired = !!bookingCfg.destination_required;
  const consentRequired = !!(bookingCfg.location_consent_required || workflowEligibility?.consent_required);
  const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

  const statusLabel = useCallback((s) => (s ? t(`sched.status.${s}`) : '—'), [t]);

  // ---- display-name resolvers ---------------------------------------------
  // Backend enrichment (`*_display_name`) is primary; config-derived name maps are the fallback,
  // then a neutral label. The raw *_id is NEVER shown as the primary label (raw ids live only in the
  // collapsed Technical Details block). Business ≠ Provider — resolved from independent fields.
  const businessName = currentBusiness?.businessName || '';
  const [showTech, setShowTech] = useState(false);

  const typeName = useCallback((a) => a?.appointment_type_display_name
    || cfgTypes.find((ty) => ty.id === a?.appointment_type_id)?.name
    || t('sched.typeFallback'), [cfgTypes, t]);
  const provName = useCallback((a) => a?.provider_display_name
    || cfgProviders.find((p) => p.staff_id === a?.provider_staff_id)?.name
    || t('sched.col.provider'), [cfgProviders, t]);
  // §19 (list+detail completion) — GENERIC per-appointment rule for showing the scheduling provider/resource
  // NAME. Show it when the resource is person/staff-oriented (Clinic/legacy tenant, provider_resource_hint
  // "staff" or unset) OR the appointment is a LEGACY (non-pack) type — so a historical Consultation keeps its
  // truthful "Provider: Dr. Dele". HIDE it only for a profile/pack-type booking (id "pack:…") under a
  // NON-person profile (e.g. Movers "equipment"), where the internal calendar anchor's stale clinical name is
  // not an operational concept — Crew (crew_staff_ids) is shown separately. No industry/name hardcoding.
  const showApptProvider = useCallback(
    (a) => shouldShowProviderName(effResourceHint) || !String(a?.appointment_type_id || '').startsWith('pack:'),
    [effResourceHint]);
  const custName = useCallback((a) => a?.customer_display_name || t('sched.col.customer'), [t]);
  const bizName = useCallback((a) => a?.business_display_name || businessName || t('sched.business'), [businessName, t]);

  const buildQuery = useCallback((atOffset) => {
    const p = new URLSearchParams();
    if (brandId) p.set('business_id', brandId);
    if (filters.status) p.set('status', filters.status);
    if (filters.provider.trim()) p.set('provider_staff_id', filters.provider.trim());
    if (filters.customer.trim()) p.set('customer_id', filters.customer.trim());
    // A picked DAY means that whole day where the user is, not an instant at UTC midnight.
    // `From 09/10` previously produced 2026-09-10T00:00Z, which is 6pm on the 9th in Edmonton, and
    // `To 09/11` produced 6pm on the 10th — so the last thirty hours of the range were silently
    // outside it. dayStart/dayEnd anchor both ends to local midnight and 23:59:59.999 instead.
    if (filters.from) p.set('start_from', dayStartIso(filters.from));
    if (filters.to) p.set('start_to', dayEndIso(filters.to));
    p.set('limit', String(PAGE_SIZE));
    p.set('offset', String(Math.max(0, Number(atOffset) || 0)));
    return p.toString();
  }, [brandId, filters]);

  const loadList = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/scheduling/appointments?${buildQuery(offset)}`, {
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(getErrorMessage({ status: resp.status, data: e }, `HTTP ${resp.status}`));
      }
      const data = await resp.json();
      // REPLACE, never merge. Appending would duplicate rows across pages and would leave rows from
      // a previous filter on screen after a narrower search.
      const rows = Array.isArray(data.appointments) ? data.appointments : [];
      setAppointments(rows);
      setPageInfo({
        // `count` is this page; `total` is every row matching the tenant AND the active filters.
        // Reporting the page as the total is exactly what hid the missing bookings.
        count: Number.isFinite(data.count) ? data.count : rows.length,
        total: Number.isFinite(data.total) ? data.total : rows.length,
        truncated: !!data.truncated,
      });
    } catch (err) {
      setError(err.message || 'error');
      setAppointments([]);
      setPageInfo({ total: 0, count: 0, truncated: false });
    } finally {
      setLoading(false);
    }
  }, [base, brandId, buildQuery, offset]);

  const loadThread = useCallback(async (conversationId) => {
    if (!conversationId) { setThreadMsgs([]); return; }
    setThreadLoading(true);
    try {
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/conversations/${encodeURIComponent(conversationId)}/messages`, {
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      if (!resp.ok) { setThreadMsgs([]); return; }
      const data = await resp.json();
      const msgs = Array.isArray(data) ? data : (data.messages || []);
      setThreadMsgs(msgs);
    } catch (e) {
      setThreadMsgs([]);
    } finally {
      setThreadLoading(false);
    }
  }, [base]);

  const openDetail = useCallback(async (id) => {
    setSelectedId(id);
    setDetail(null);
    setThreadMsgs(null);
    setDetailLoading(true);
    try {
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/scheduling/appointments/${encodeURIComponent(id)}?business_id=${encodeURIComponent(brandId)}`, {
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(getErrorMessage({ status: resp.status, data: e }, `HTTP ${resp.status}`));
      }
      const data = await resp.json();
      const appt = data.appointment || null;
      setDetail(appt);
      // C (truthful materialization): only load messages for a REAL materialized canonical thread.
      // No fallback to the raw appointment.conversation_id — a non-materialized id must not be fetched.
      loadThread(appt?.thread?.materialized ? appt.thread.conversation_id : null);
    } catch (err) {
      toast({ title: t('common.error'), description: err.message || 'error', variant: 'destructive' });
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [base, brandId, loadThread, t, toast]);

  // Deliberately NOT keyed on loadList: it changes identity on every filter keystroke, which would
  // refetch while the user is still typing. A query runs when the tenant changes, when the page
  // changes, or when Search/Clear explicitly asks for one — the same shape the original effect used.
  // (No eslint-disable here: the react-hooks plugin is not registered in this project's build lint
  //  config, so the pragma itself fails the production build.)
  useEffect(() => { loadList(); }, [brandId, offset, queryNonce]);

  /** Run the CURRENT filter values from the first page. Both state updates are batched, so this
   *  issues exactly one request even when the offset was already 0. */
  const runSearch = useCallback(() => {
    setOffset(0);
    setQueryNonce((n) => n + 1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ status: '', provider: '', customer: '', from: '', to: '' });
    setOffset(0);
    setQueryNonce((n) => n + 1);
  }, []);

  const canPrev = offset > 0;
  const canNext = pageInfo.truncated;
  const rangeStart = pageInfo.count > 0 ? offset + 1 : 0;
  const rangeEnd = offset + pageInfo.count;

  // GOV-7 Issue B — resolve the EFFECTIVE booking contract (profile/pack types + terminology) from the
  // /config response the backend already returns (effective_config, computed by the ONE canonical resolver).
  // Effective appointment types (e.g. the pack:movers:* set) source the New-Booking dropdown so a Movers
  // tenant never books a legacy clinic-era "Consultation". Empty effective set → fall back to the tenant's
  // legacy config types (back-compat). NEVER hardcodes any industry type name here.
  const applyEffective = useCallback((data) => {
    const fields = data?.effective_config?.fields || {};
    const effTypes = Array.isArray(fields.appointment_types?.value) ? fields.appointment_types.value : [];
    const cfgOwn = Array.isArray(data?.config?.appointment_types) ? data.config.appointment_types : [];
    const types = effTypes.length > 0 ? effTypes : cfgOwn;
    setCfgTypes(types);
    if (Array.isArray(data?.config?.providers)) setCfgProviders(data.config.providers);
    setEffResourceLabel(fields.resource_display_label?.value || '');
    setEffCustomerLabel(fields.customer_display_label?.value || '');
    setEffResourceHint(fields.provider_resource_hint?.value || '');
    // The SCHEDULING timezone (SchedulingConfig.timezone). Effective config wins when the profile
    // publishes one; otherwise the tenant's own stored value. Never defaulted to a guess — an absent
    // value renders in the device zone and is LABELLED as such rather than mislabelled as tenant time.
    setCfgTimezone(fields.timezone?.value || data?.config?.timezone || '');
    return types;
  }, []);

  // Load config-derived provider/type name maps on mount (fallback for list/detail names before the
  // backend `*_display_name` enrichment is deployed). Reuses the same cfg state as create-booking.
  const loadCfgMaps = useCallback(async () => {
    if (!brandId) return;
    try {
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/scheduling/config?business_id=${encodeURIComponent(brandId)}`, {
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      applyEffective(data);
    } catch (e) { /* fallback stays on neutral labels */ }
  }, [base, brandId, applyEffective]);

  useEffect(() => { loadCfgMaps(); }, [loadCfgMaps]);

  // ---- actions (POST) — success shown ONLY after resp.ok ----
  const runAction = useCallback(async (path, body, successKey) => {
    if (!selectedId) return;
    setActing(true);
    try {
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/scheduling/appointments/${encodeURIComponent(selectedId)}/${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify({ business_id: brandId, ...body }),
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(getErrorMessage({ status: resp.status, data: e }, `HTTP ${resp.status}`));
      }
      toast({ title: t(successKey) });
      setModal(null); setReason(''); setOverride(false); setChosenSlot(null); setSlots(null);
      await loadList();
      await openDetail(selectedId);
    } catch (err) {
      toast({ title: t('sched.toast.failed'), description: err.message || 'error', variant: 'destructive' });
    } finally {
      setActing(false);
    }
  }, [base, brandId, selectedId, loadList, openDetail, t, toast]);

  const loadSlots = useCallback(async () => {
    if (!detail) return;
    setSlotsLoading(true); setSlots(null); setChosenSlot(null);
    try {
      const token = await _getToken();
      const p = new URLSearchParams();
      p.set('business_id', brandId);
      p.set('appointment_type_id', detail.appointment_type_id || '');
      if (detail.provider_staff_id) p.set('provider_staff_id', detail.provider_staff_id);
      const resp = await fetch(`${base}/api/v1/scheduling/availability?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      if (!resp.ok) { setSlots([]); return; }
      const data = await resp.json();
      setSlots(Array.isArray(data.slots) ? data.slots : []);
    } catch (e) {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [base, brandId, detail]);

  // P7 — enter this appointment's remote consultation as the ASSIGNED Operator.
  //
  // `encounter.can_join` drew the button; it is NOT a ticket. Between the read and this click the
  // booking may have been reassigned, cancelled, or run out of its window, so the server decides
  // again here and a refusal is normal. On refusal we re-read the appointment rather than retrying
  // with what the page is holding, so the action reflects what is now true.
  //
  // Audio vs video is NOT decided here. It travels on the server's contract as `call_type`, derived
  // from the booking's authorized consultation_mode -- so an audio consultation cannot be escalated
  // to video by anything this client does.
  const joinEncounter = useCallback(async () => {
    const id = detail && detail.appointment_id;
    if (!id || joining || !rtc || typeof rtc.joinMediaRoom !== 'function') return;
    setJoining(true); setJoinErr(false);
    try {
      const token = await _getToken();
      const resp = await fetch(
        `${base}/api/v1/scheduling/appointments/${encodeURIComponent(id)}/encounter/join`
        + `?business_id=${encodeURIComponent(brandId)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } });
      if (!resp.ok) {
        // Denied, stale or gone: no media start, no armed answerer, no Connected state.
        setJoinErr(true);
        await openDetail(id);
        return;
      }
      const data = await resp.json();
      const media = data && data.media;
      if (!media || !media.call_id) { setJoinErr(true); await openDetail(id); return; }
      rtc.joinMediaRoom(media, { businessId: brandId, customerName: custName(detail) });
    } catch (e) {
      setJoinErr(true);
    } finally {
      setJoining(false);
    }
  }, [base, brandId, detail, joining, rtc, openDetail, custName]);

  // ---- create-booking flow (real POST /appointments; customer resolved server-side) ----
  const openCreate = useCallback(async () => {
    setCForm({ type: '', provider: '', customerId: '', phone: '', email: '', reason: '', pickup: '', destination: '' });
    setCreateCrew([]);
    setCustQuery(''); setCustResults(null); setCustSelected(null);
    setCSlots(null); setCChosen(null); setCreateOpen(true);
    try {
      const token = await _getToken();
      const resp = await fetch(`${base}/api/v1/scheduling/config?business_id=${encodeURIComponent(brandId)}`, {
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      if (resp.ok) {
        applyEffective(await resp.json());   // effective (profile/pack) types + terminology; falls back to config types
      } else { setCfgTypes([]); setCfgProviders([]); setEffResourceLabel(''); setEffCustomerLabel(''); }
    } catch (e) { setCfgTypes([]); setCfgProviders([]); setEffResourceLabel(''); setEffCustomerLabel(''); }
  }, [base, brandId, applyEffective]);

  // Customer typeahead — reuses the ONE tenant-guarded customer authority (GET /customers/business/{id}?search=).
  // Displays NAME (+ phone/email for disambiguation) and sets customer_id from the selected result; the raw
  // free-text "Customer ID" input is gone (CUSTOMER_RAW_ID_INPUT_REQUIRED=NO). New-by-phone/email still works.
  const searchCustomers = useCallback(async (q) => {
    if (!brandId || !q || !q.trim()) { setCustResults(null); setCustError(false); return; }

    // SUPERSEDE, DO NOT RACE. The debounce alone only spaced the requests out; it never cancelled
    // one already in flight, and nothing marked which response belonged to the newest query. A
    // slower earlier search could therefore land last and overwrite a newer one — the field
    // appearing to "stick" on a stale patient. The sequence id decides who owns the UI; the abort
    // stops the superseded request costing anything.
    try { custAbortRef.current?.abort(); } catch (_) { /* nothing in flight */ }
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    custAbortRef.current = ctrl;
    const seq = ++custSeqRef.current;
    const mine = () => seq === custSeqRef.current;

    setCustSearching(true);
    setCustError(false);
    try {
      const token = await _getToken();
      if (!mine()) return;                      // a newer query started during the token wait
      const resp = await fetch(`${base}/api/v1/customers/business/${encodeURIComponent(brandId)}?search=${encodeURIComponent(q.trim())}`, {
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
        signal: ctrl ? ctrl.signal : undefined,
      });
      if (!mine()) return;
      // A FAILURE IS REPORTED AS A FAILURE. Results are left untouched so the operator keeps
      // whatever was last valid, and the field stays editable and retryable.
      if (!resp.ok) { setCustError(true); return; }
      const data = await resp.json();
      if (!mine()) return;
      setCustResults(Array.isArray(data.customers) ? data.customers : []);
    } catch (e) {
      if (e && e.name === 'AbortError') return;  // superseded on purpose — never clear results
      if (!mine()) return;
      setCustError(true);
    } finally {
      if (mine()) setCustSearching(false);
    }
  }, [base, brandId]);

  // Debounced query → search. Clearing the box clears the selection so a raw id can never linger.
  useEffect(() => {
    if (!createOpen) return undefined;
    const q = custQuery;
    if (!q || !q.trim()) { setCustResults(null); setCustError(false); return undefined; }
    if (custSelected && custSelected.name === q) return undefined;   // don't re-search the chosen label
    const h = setTimeout(() => searchCustomers(q), 250);
    return () => clearTimeout(h);
  }, [custQuery, createOpen, custSelected, searchCustomers]);

  const selectCustomer = useCallback((c) => {
    const id = c.customer_id || c.id || '';
    setCustSelected(c);
    setCustQuery(c.name || id);
    setCustResults(null);
    setCustError(false);          // a chosen patient supersedes any earlier failure
    setCForm((f) => ({ ...f, customerId: id }));
  }, []);

  const loadCreateSlots = useCallback(async () => {
    if (!cForm.type) { toast({ title: t('sched.create.needType'), variant: 'destructive' }); return; }
    setCSlotsLoading(true); setCSlots(null); setCChosen(null);
    try {
      const token = await _getToken();
      const p = new URLSearchParams();
      p.set('business_id', brandId);
      p.set('appointment_type_id', cForm.type);
      if (cForm.provider.trim()) p.set('provider_staff_id', cForm.provider.trim());
      const resp = await fetch(`${base}/api/v1/scheduling/availability?${p.toString()}`, {
        headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() },
      });
      if (!resp.ok) { setCSlots([]); return; }
      const data = await resp.json();
      setCSlots(Array.isArray(data.slots) ? data.slots : []);
    } catch (e) { setCSlots([]); } finally { setCSlotsLoading(false); }
  }, [base, brandId, cForm.type, cForm.provider, t, toast]);

  const doCreate = useCallback(async () => {
    if (!cForm.type) { toast({ title: t('sched.create.needType'), variant: 'destructive' }); return; }
    if (!cChosen) { toast({ title: t('sched.resched.pick'), variant: 'destructive' }); return; }
    if (!cForm.customerId.trim() && !cForm.phone.trim() && !cForm.email.trim()) {
      toast({ title: t('sched.create.needCustomer'), variant: 'destructive' }); return;
    }
    if (pickupRequired && !cForm.pickup.trim()) { toast({ title: t('sched.create.needPickup'), variant: 'destructive' }); return; }
    if (destinationRequired && !cForm.destination.trim()) { toast({ title: t('sched.create.needDestination'), variant: 'destructive' }); return; }
    setCreating(true);
    try {
      const token = await _getToken();
      const body = {
        business_id: brandId,
        provider_staff_id: cChosen.provider_staff_id,
        appointment_type_id: cForm.type,
        start_ts: cChosen.start_ts,
        customer_id: cForm.customerId.trim() || undefined,
        phone: cForm.phone.trim() || undefined,
        email: cForm.email.trim() || undefined,
        reason: cForm.reason.trim() || undefined,
      };
      // Profile-driven field-service context sent through the EXISTING canonical fields (no new authority).
      // Gated on the effective requirement so Clinic/base bookings send nothing new. Consent is NOT self-granted
      // here (BUSINESS_MANAGER_CAN_SELF_GRANT_CUSTOMER_LOCATION_CONSENT=NO) — it stays the customer grant flow.
      if (pickupRequired && cForm.pickup.trim()) body.service_location = { address: cForm.pickup.trim() };
      if (destinationRequired && cForm.destination.trim()) body.destination_location = { address: cForm.destination.trim() };
      const resp = await fetch(`${base}/api/v1/scheduling/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
        body: JSON.stringify(body),
      });
      if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(getErrorMessage({ status: resp.status, data: e }, `HTTP ${resp.status}`)); }
      // OPTIONAL crew pre-assignment: booking + assignment stay DISTINCT authorities. If crew was chosen, the
      // appointment is created FIRST (unchanged create contract), then assigned via the canonical crew endpoint
      // (crew_staff_ids). If the assign call fails, the booking still exists unassigned — crew can be assigned
      // later in the job panel (no fabricated crew, order preserved for phone-source precedence).
      const created = await resp.json().catch(() => ({}));
      const newApptId = created?.appointment?.id || created?.id;
      if (newApptId && Array.isArray(createCrew) && createCrew.length > 0) {
        try {
          await fetch(`${base}/api/v1/scheduling/appointments/${encodeURIComponent(newApptId)}/assignment`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...tenantHeaders() },
            body: JSON.stringify({ business_id: brandId, crew_staff_ids: createCrew }),
          });
        } catch (e) { /* booking created; crew assignment can be completed later in the job panel */ }
      }
      toast({ title: t('sched.toast.created') });
      setCreateOpen(false);
      await loadList();
    } catch (err) {
      toast({ title: t('sched.toast.failed'), description: err.message || 'error', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  }, [base, brandId, cForm, cChosen, createCrew, pickupRequired, destinationRequired, loadList, t, toast]);

  const openModal = (kind) => {
    setReason(''); setOverride(false); setChosenSlot(null); setSlots(null);
    setModal(kind);
    if (kind === 'reschedule') loadSlots();
  };

  const aa = detail?.allowed_actions || {};
  const canConfirm = !!aa.can_confirm;
  const canDecline = !!aa.can_decline;
  const canCancel = !!aa.can_cancel;
  const canReschedule = !!aa.can_reschedule;
  const canOverride = !!aa.can_override;
  const anyAction = canConfirm || canDecline || canCancel || canReschedule;

  const results = useMemo(() => appointments.length, [appointments]);

  if (!currentBusiness) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    // P1-B: the route host is `flex-1 overflow-hidden`, so this page must own its own vertical scroll
    // (h-full + overflow-y-auto) — otherwise appointments below the fold are clipped and unreachable by
    // wheel/touchpad/PageDown. Mirrors the Unified Desktop scroll container.
    <div className="h-full overflow-y-auto p-6 md:p-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Calendar className="h-7 w-7 text-purple-600" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('page.scheduling.title')}</h1>
            <p className="text-gray-600">
              {t('page.scheduling.subtitle', { business: currentBusiness.businessName || currentBusiness.brandId || '' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadList} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          <Button className="bg-purple-600 hover:bg-purple-700" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            {t('sched.create.button')}
          </Button>
        </div>
      </div>

      {/* W1 — PERSONAL calendar connection. It lives HERE, on the provider's own Scheduling
          surface, and not in Scheduling Settings: Settings is a TENANT-configuration screen whose
          nav entry is shown only to admin and manager, so an operator — who in the Clinic profile
          IS the provider whose calendar this is — could never reach their own connection there.
          A personal credential must be managed by the person it belongs to.

          The server binds the connection to the AUTHENTICATED caller's own staff id, so whoever
          opens this manages only their own calendar; there is no parameter for whose. The section
          renders nothing when the tenant lacks the capability or the caller's role may not use it. */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <PersonalCalendarSection
            brandId={brandId}
            getToken={_getToken}
            tenantHeaders={tenantHeaders}
            t={t}
          />
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div>
              <label className="text-xs text-gray-500">{t('sched.filter.status')}</label>
              <select
                className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
                value={filters.status}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="">{t('sched.filter.allStatuses')}</option>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">{t('sched.filter.provider')}</label>
              <Input className="mt-1 h-9" value={filters.provider} onChange={(e) => setFilters((f) => ({ ...f, provider: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500">{t('sched.filter.customer')}</label>
              <Input className="mt-1 h-9" value={filters.customer} onChange={(e) => setFilters((f) => ({ ...f, customer: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500">{t('sched.filter.from')}</label>
              {/* A DATE, not a datetime. The previous datetime-local yielded an empty value until a
                  time was also entered, so picking just the two dates sent no range at all and the
                  unfiltered list stayed on screen — which is exactly what was reported. */}
              <Input type="date" data-testid="sched-from" className="mt-1 h-9" value={filters.from}
                     onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-gray-500">{t('sched.filter.to')}</label>
              <Input type="date" data-testid="sched-to" className="mt-1 h-9" value={filters.to}
                     onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1" data-testid="sched-search" onClick={runSearch} disabled={loading}>{t('common.search')}</Button>
              <Button variant="ghost" data-testid="sched-clear" onClick={clearFilters}>
                {t('sched.filter.clear')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center justify-between">
                <span>{t('page.scheduling.title')}</span>
                <span className="text-xs font-normal text-gray-500" data-testid="sched-range">
                  {pageInfo.total > 0
                    ? t('sched.showingRange', { from: rangeStart, to: rangeEnd, total: pageInfo.total })
                    : t('sched.results', { count: results })}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-16 text-center text-gray-500">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
                  {t('sched.loading')}
                </div>
              ) : error ? (
                <div className="py-12 text-center">
                  <p className="text-red-700 mb-3">{t('sched.failedLoad')}</p>
                  <p className="text-xs text-gray-500 mb-4 break-all">{error}</p>
                  <Button onClick={loadList}>{t('common.retry')}</Button>
                </div>
              ) : appointments.length === 0 ? (
                <div className="py-16 text-center">
                  <Calendar className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">{t('sched.empty')}</p>
                  <p className="text-xs text-gray-400 mt-1">{t('sched.emptyHint')}</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {appointments.map((a) => (
                    <button
                      key={a.appointment_id}
                      onClick={() => openDetail(a.appointment_id)}
                      className={`w-full text-left py-3 px-2 hover:bg-purple-50/60 rounded-md transition ${selectedId === a.appointment_id ? 'bg-purple-50' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <CalendarClock className="h-4 w-4 text-gray-400 shrink-0" />
                            <span className="truncate">{fmt(a.start_ts)}</span>
                          </div>
                          <div className="text-xs text-gray-500 truncate mt-0.5">
                            {t('sched.col.type')}: {typeName(a)}{showApptProvider(a) && <> · {t('sched.col.provider')}: {provName(a)}</>}
                          </div>
                          <div className="text-xs text-gray-400 truncate">
                            {t('sched.col.customer')}: {custName(a)}
                          </div>
                        </div>
                        <Badge className={STATUS_COLOR[a.status] || 'bg-gray-100 text-gray-700'} variant="secondary">
                          {statusLabel(a.status)}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Pagination. Rendered whenever the tenant has more bookings than one page holds, so
                  the existence of further results is visible rather than something a user has to
                  suspect. Both controls move the SERVER offset — nothing is sliced in the browser. */}
              {!loading && !error && (pageInfo.truncated || offset > 0) && (
                <div className="flex items-center justify-between gap-3 pt-3 mt-3 border-t border-gray-100"
                     data-testid="sched-pager">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="sched-prev"
                    disabled={!canPrev || loading}
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  >
                    {t('sched.page.prev')}
                  </Button>
                  <span className="text-xs text-gray-500" data-testid="sched-page-position">
                    {t('sched.showingRange', { from: rangeStart, to: rangeEnd, total: pageInfo.total })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="sched-next"
                    disabled={!canNext || loading}
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  >
                    {t('sched.page.next')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          <Card className="sticky top-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-purple-600" />
                {t('sched.detail.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedId ? (
                <p className="py-10 text-center text-sm text-gray-500">{t('sched.detail.select')}</p>
              ) : detailLoading || !detail ? (
                <div className="py-10 text-center text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Core fields */}
                  <div className="space-y-1.5 text-sm">
                    <Row label={t('sched.col.status')} value={<Badge className={STATUS_COLOR[detail.status] || 'bg-gray-100 text-gray-700'} variant="secondary">{statusLabel(detail.status)}</Badge>} />
                    <Row label={t('sched.detail.start')} value={fmt(detail.start_ts)} />
                    <Row label={t('sched.detail.end')} value={fmt(detail.end_ts)} />
                    <Row label={t('sched.business')} value={bizName(detail)} />
                    <Row label={t('sched.detail.type')} value={typeName(detail)} />
                    {showApptProvider(detail) && (
                      <Row label={t('sched.detail.provider')} value={<span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5 text-gray-400" />{provName(detail)}</span>} />
                    )}
                    <Row label={t('sched.detail.customer')} value={custName(detail)} />
                    <Row label={t('sched.detail.mode')} value={detail.scheduling_mode || '—'} />
                    {/* Resource row: person/staff or legacy → truthful name; non-person pack booking → neutral '—'
                        (the operational Crew is shown in the assignment panel, not as this stale anchor name). */}
                    <Row label={t('sched.detail.resource')} value={showApptProvider(detail) ? (detail.resource_display_name || (detail.resource_type === 'staff' ? provName(detail) : (detail.resource_type || '—'))) : '—'} />
                    <Row label={t('sched.detail.confirmationSource')} value={detail.confirmation_source || '—'} />
                    <Row label={t('sched.detail.created')} value={fmt(detail.created_at)} />
                    <Row label={t('sched.detail.updated')} value={fmt(detail.updated_at)} />
                  </div>

                  {/* Technical Details — raw canonical ids for API calls / audit / debug ONLY. */}
                  <div className="border-t pt-2">
                    <button
                      type="button"
                      onClick={() => setShowTech((v) => !v)}
                      className="flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase hover:text-gray-600"
                    >
                      {showTech ? '▲' : '▼'} {t('sched.technicalDetails')}
                    </button>
                    {showTech && (
                      // D3: normal Manager/Operator scheduling UI — friendly reference + resolved names, never raw entity ids.
                      <div className="mt-1.5 space-y-1 text-xs text-gray-500">
                        {/* E2: the SERVER's reference. This row used to build one on the client as
                            'APT-' + appointment_id.slice(-6) — which put six characters of the internal
                            id on screen under a friendly label, and made the client a second reference
                            authority. The server now owns the format (services/human_reference.py) and
                            resolves a stable value even for bookings made before the field existed. */}
                        <Row label="Reference" value={detail.reference || '—'} />
                        <Row label={t('sched.col.customer')} value={detail.customer_display_name || '—'} />
                        {showApptProvider(detail) && <Row label={t('sched.col.provider')} value={provName(detail)} />}
                        <Row label="Type" value={detail.appointment_type_display_name || '—'} />
                      </div>
                    )}
                  </div>

                  {/* P7 — the consultation. Mode and joinability are the SERVER's answers; this
                      renders them and computes neither. An in-person booking gets a label and no
                      remote action, because there is no remote action to offer. */}
                  {(() => {
                    const enc = detail.encounter || {};
                    const mode = enc.consultation_mode || detail.consultation_mode || 'in_person';
                    const modeLabel = mode === 'video' ? t('schedcfg.mode.video')
                      : mode === 'audio' ? t('schedcfg.mode.audio') : t('schedcfg.mode.inPerson');
                    const canJoin = enc.remote === true && enc.can_join === true
                      && !!rtc && typeof rtc.joinMediaRoom === 'function';
                    return (
                      <div className="border-t pt-2">
                        <Row label={t('sched.consult.label')} value={modeLabel} />
                        {enc.remote === true && (
                          <div className="mt-1.5">
                            {canJoin ? (
                              <Button size="sm" onClick={joinEncounter} disabled={joining}>
                                {joining ? (
                                  <span className="inline-flex items-center gap-1">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    {t('sched.consult.joining')}
                                  </span>
                                ) : (mode === 'video'
                                  ? t('sched.consult.joinVideo') : t('sched.consult.joinAudio'))}
                              </Button>
                            ) : (
                              <p className="text-xs text-gray-500">{t('sched.consult.unavailable')}</p>
                            )}
                            {joinErr && (
                              <p className="mt-1 text-xs text-red-600">{t('sched.consult.failed')}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Requirements */}
                  {(detail.requires_approval || detail.requires_payment || detail.requires_document) && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{t('sched.detail.requirements')}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {detail.requires_approval && <Badge variant="secondary" className="bg-indigo-50 text-indigo-700">{t('sched.req.approval')}</Badge>}
                        {detail.requires_payment && <Badge variant="secondary" className="bg-indigo-50 text-indigo-700">{t('sched.req.payment')}</Badge>}
                        {detail.requires_document && <Badge variant="secondary" className="bg-indigo-50 text-indigo-700">{t('sched.req.document')}</Badge>}
                      </div>
                    </div>
                  )}

                  {/* H5 — capture consent, VIEW ONLY. Staff see where the PATIENT stands so the
                      room is not a surprise. No control here by construction: the backend strips
                      can_grant/can_withdraw from the staff projection and exposes no staff route
                      that could set the subject's consent. Rendered from the governed policy alone,
                      so it does not depend on the field-service panel below. */}
                  {detail.capture_consent?.items?.length > 0 && (
                    <div data-testid="capture-consent">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1">
                        <ShieldCheck className="h-3.5 w-3.5" />{t('sched.captureConsent.title')}
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        {detail.capture_consent.items.map((it) => (
                          <div key={it.capture_type} data-testid={`capture-consent-${it.capture_type}`}>
                            <span className="font-medium">{t(`sched.captureConsent.type.${it.capture_type}`)}:</span>{' '}
                            <b>{t(`sched.captureConsent.state.${it.state}`)}</b>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* M4 — start/stop the governed recording for THIS appointment.
                      Placed after the consent standing above, because that is the order the decision
                      is actually made in: the provider sees where the patient stands, then acts.
                      It renders only what `capture_readiness` — computed server-side — permits, and
                      it drives the SAME producer as the control inside the call, so a recording
                      started here is already running when the provider joins. `rtc?.localStream` is
                      passed so that if a call is ALREADY up on this page, the call's microphone is
                      the source and a second one is never opened. */}
                  <AppointmentCaptureControl
                    appointmentId={detail.appointment_id}
                    businessId={brandId}
                    readiness={detail.capture_readiness}
                    captureApi={captureApi}
                    callStream={(rtc && rtc.localStream) || null}
                  />

                  {/* Policies */}
                  {detail.policy && Object.keys(detail.policy).length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-1 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />{t('sched.policy.title')}</div>
                      <div className="text-xs text-gray-600 space-y-1">
                        {detail.policy.cancellation && (
                          <div><span className="font-medium">{t('sched.policy.cancellation')}:</span> {t('sched.policy.allowCustomerCancel')} {yesNo(detail.policy.cancellation.allow_customer_cancel, t)} · {t('sched.policy.minNotice')} {num(detail.policy.cancellation.min_notice_minutes)} · {t('sched.policy.meetsNotice')} {yesNo(detail.policy.cancellation.meets_notice, t)}</div>
                        )}
                        {detail.policy.reschedule && (
                          <div><span className="font-medium">{t('sched.policy.reschedule')}:</span> {t('sched.policy.allowCustomerReschedule')} {yesNo(detail.policy.reschedule.allow_customer_reschedule, t)} · {t('sched.policy.minNotice')} {num(detail.policy.reschedule.min_notice_minutes)} · {t('sched.policy.maxReschedules')} {detail.policy.reschedule.max_reschedules == null ? t('sched.policy.unlimited') : detail.policy.reschedule.max_reschedules}</div>
                        )}
                        {detail.policy.confirmation && (
                          <div><span className="font-medium">{t('sched.policy.confirmation')}:</span> {t('sched.policy.mode')} {detail.policy.confirmation.mode || '—'}{detail.policy.confirmation.expires_after_minutes != null ? ` · ${t('sched.policy.expiresAfter')} ${detail.policy.confirmation.expires_after_minutes}` : ''}</div>
                        )}
                        {Array.isArray(detail.policy.knowledge_refs) && detail.policy.knowledge_refs.length > 0 && (
                          <div>
                            <span className="font-medium">{t('sched.policy.knowledgeRefs')}:</span> {detail.policy.knowledge_refs.join(', ')}
                            <div className="text-[11px] text-gray-400 italic">{t('sched.policy.knowledgeRefsHint')}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Engine metadata (read-only, future engine) */}
                  {detail.future_engine && (detail.future_engine.workflow_template_id || detail.future_engine.industry_pack_id) && (
                    <div className="text-[11px] text-gray-400">
                      <span className="uppercase font-semibold">{t('sched.engine.title')}:</span>{' '}
                      {detail.future_engine.workflow_template_id || t('sched.engine.none')} / {detail.future_engine.industry_pack_id || t('sched.engine.none')}
                    </div>
                  )}

                  {/* GOV-7 field-service (Movers) job panel — renders ONLY when the backend eligibility gate
                      (workflow_enabled) is true; hidden for tenants without an applicable pinned profile. */}
                  <MoversJobPanel
                    appointment={detail}
                    eligibility={workflowEligibility}
                    api={_api}
                    staffRoster={staffRoster}
                    businessDisplayName={bizName(detail)}
                    onChanged={() => openDetail(selectedId)}
                  />

                  {/* D5b/D6 — document requirement. Renders only when the BACKEND says this booking
                      needs one; every button comes from the server's allowed_actions. */}
                  <DocumentPanel
                    appointmentId={selectedId}
                    businessId={brandId}
                    api={_api}
                    t={t}
                    onChanged={() => openDetail(selectedId)}
                  />

                  {/* P2/Q2 — governed clinical documentation. Renders only when the tenant's
                      PROFILE permits draft templates; every section, every editable flag and every
                      button comes from the server projection. Sits beside the document panel rather
                      than in a workspace of its own. */}
                  <DraftPanel
                    appointmentId={selectedId}
                    businessId={brandId}
                    api={_api}
                    t={t}
                    onChanged={() => openDetail(selectedId)}
                  />

                  {/* Actions — rendered ONLY from server allowed_actions */}
                  <div className="pt-1 border-t border-gray-100">
                    {!anyAction ? (
                      <p className="text-xs text-gray-400 pt-3">{t('sched.action.noneAvailable')}</p>
                    ) : (
                      <div className="flex flex-wrap gap-2 pt-3">
                        {canConfirm && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openModal('confirm')}><CheckCircle2 className="h-4 w-4 mr-1" />{t('sched.action.confirm')}</Button>}
                        {canDecline && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => openModal('decline')}><XCircle className="h-4 w-4 mr-1" />{t('sched.action.decline')}</Button>}
                        {canReschedule && <Button size="sm" variant="outline" onClick={() => openModal('reschedule')}><CalendarClock className="h-4 w-4 mr-1" />{t('sched.action.reschedule')}</Button>}
                        {canCancel && <Button size="sm" variant="outline" className="text-gray-700" onClick={() => openModal('cancel')}><Ban className="h-4 w-4 mr-1" />{t('sched.action.cancel')}</Button>}
                      </div>
                    )}
                  </div>

                  {/* Thread (existing db.messages surface) */}
                  <div className="pt-2 border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2 flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />{t('sched.thread.title')}</div>
                    {!(detail.thread?.conversation_id || detail.conversation_id) ? (
                      <p className="text-xs text-gray-400">{t('sched.thread.none')}</p>
                    ) : threadLoading || threadMsgs === null ? (
                      <p className="text-xs text-gray-400">{t('sched.thread.loading')}</p>
                    ) : threadMsgs.length === 0 ? (
                      <p className="text-xs text-gray-400">{t('sched.thread.empty')}</p>
                    ) : (
                      <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                        {threadMsgs.map((m, i) => {
                          const kind = m.sender_type || m.senderType;
                          const text = m.message || m.content || '';
                          const ts = m.timestamp;
                          if (kind === 'system') {
                            return (
                              <div key={m.message_id || m.id || i} className="text-center">
                                <span className="text-[11px] text-gray-500 italic bg-gray-100 rounded-full px-2 py-0.5">⚙ {text}</span>
                              </div>
                            );
                          }
                          return (
                            <div key={m.message_id || m.id || i} className="text-xs">
                              <span className="font-medium text-gray-700">{kind === 'customer' ? t('sched.detail.customer') : kind === 'ai' ? 'AI' : (kind || '')}</span>
                              <span className="text-gray-400"> · {fmt(ts)}</span>
                              <div className="text-gray-600">{text}</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* E9 — WAITLIST. Rendered ONLY when the capability is usable for this tenant, so a tenant
              without the Advanced Scheduling add-on sees no trace of it. Read-mostly: the queue is
              informational plus a withdraw action; nothing here books. */}
          {waitlistAvailable && (
            <Card className="mt-6" data-testid="waitlist-panel">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{t('sched.waitlist.title')}</span>
                  <span className="text-xs font-normal text-gray-500"
                        data-testid="waitlist-count">{waitlist.length}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {waitlist.length === 0 ? (
                  <p className="text-sm text-gray-500" data-testid="waitlist-empty">{t('sched.waitlist.empty')}</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {waitlist.map((w) => (
                      <li key={w.reference} className="py-2 flex items-start justify-between gap-3"
                          data-testid={`waitlist-row-${w.reference}`}>
                        <div className="min-w-0">
                          {/* The READABLE reference, never the internal entry id. */}
                          <p className="text-sm font-medium text-gray-900">{w.reference}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {/* NAMES, not ids — the same display authority the rest of this screen uses. */}
                            {w.customer_display_name || t('sched.waitlist.customer')}
                            {w.appointment_type_display_name ? ` · ${w.appointment_type_display_name}` : ''}
                            {w.provider_display_name ? ` · ${w.provider_display_name}` : ''}
                          </p>
                          {(w.desired_from || w.desired_to) && (
                            <p className="text-xs text-gray-400">
                              {t('sched.waitlist.window')}: {w.desired_from || '—'} → {w.desired_to || '—'}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="text-xs">{w.status}</Badge>
                          <Button size="sm" variant="ghost" className="text-red-600 h-8"
                                  disabled={waitlistBusy === w.id}
                                  data-testid={`waitlist-remove-${w.reference}`}
                                  onClick={() => setWaitlistStatus(w.id, 'withdrawn')}>
                            {t('sched.waitlist.remove')}
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ---- Action modals ---- */}
      {modal && detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !acting && setModal(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {modal === 'confirm' && (
              <>
                <h3 className="text-lg font-semibold mb-2">{t('sched.confirm.title')}</h3>
                <p className="text-sm text-gray-600 mb-4">{t('sched.confirm.body')}</p>
                <ModalButtons t={t} acting={acting} onCancel={() => setModal(null)}
                  onSubmit={() => runAction('confirm', {}, 'sched.toast.confirmed')}
                  submitLabel={t('sched.action.confirm')} submitClass="bg-green-600 hover:bg-green-700" />
              </>
            )}
            {modal === 'decline' && (
              <>
                <h3 className="text-lg font-semibold mb-3">{t('sched.decline.title')}</h3>
                <label className="text-xs text-gray-500">{t('sched.decline.reason')}</label>
                <Input className="mt-1 mb-4" value={reason} onChange={(e) => setReason(e.target.value)} />
                <ModalButtons t={t} acting={acting} onCancel={() => setModal(null)}
                  onSubmit={() => runAction('decline', { reason: reason || undefined }, 'sched.toast.declined')}
                  submitLabel={t('sched.decline.submit')} submitClass="bg-red-600 hover:bg-red-700" />
              </>
            )}
            {modal === 'cancel' && (
              <>
                <h3 className="text-lg font-semibold mb-3">{t('sched.cancel.title')}</h3>
                <label className="text-xs text-gray-500">{t('sched.cancel.reason')}</label>
                <Input className="mt-1 mb-3" value={reason} onChange={(e) => setReason(e.target.value)} />
                {canOverride && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
                    <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                    {t('sched.cancel.override')}
                  </label>
                )}
                <ModalButtons t={t} acting={acting} onCancel={() => setModal(null)}
                  onSubmit={() => runAction('cancel', { reason: reason || undefined, override }, 'sched.toast.cancelled')}
                  submitLabel={t('sched.cancel.submit')} submitClass="bg-gray-800 hover:bg-gray-900" />
              </>
            )}
            {modal === 'reschedule' && (
              <>
                <h3 className="text-lg font-semibold mb-1">{t('sched.resched.title')}</h3>
                <p className="text-xs text-gray-500 mb-3">{t('sched.resched.pick')}</p>
                {slotsLoading || slots === null ? (
                  <p className="text-sm text-gray-500 py-4 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />{t('sched.resched.loading')}</p>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center">{t('sched.resched.none')}</p>
                ) : (
                  <div className="max-h-52 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100 mb-3">
                    {slots.map((s, i) => {
                      const key = `${s.provider_staff_id}|${s.start_ts}`;
                      const sel = chosenSlot && chosenSlot.start_ts === s.start_ts && chosenSlot.provider_staff_id === s.provider_staff_id;
                      return (
                        <button key={key + i} onClick={() => setChosenSlot(s)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-50 ${sel ? 'bg-purple-100' : ''}`}>
                          {fmt(s.start_ts)} – {fmt(s.end_ts)}{shouldShowProviderName(effResourceHint) && <> · {provName(s)}</>}
                        </button>
                      );
                    })}
                  </div>
                )}
                <label className="text-xs text-gray-500">{t('sched.resched.reason')}</label>
                <Input className="mt-1 mb-3" value={reason} onChange={(e) => setReason(e.target.value)} />
                {canOverride && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
                    <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                    {t('sched.resched.override')}
                  </label>
                )}
                <ModalButtons t={t} acting={acting} disabled={!chosenSlot} onCancel={() => setModal(null)}
                  onSubmit={() => runAction('reschedule', {
                    new_start_ts: chosenSlot.start_ts,
                    new_provider_staff_id: chosenSlot.provider_staff_id && chosenSlot.provider_staff_id !== detail.provider_staff_id ? chosenSlot.provider_staff_id : undefined,
                    reason: reason || undefined,
                    override,
                  }, 'sched.toast.rescheduled')}
                  submitLabel={t('sched.resched.submit')} submitClass="bg-purple-600 hover:bg-purple-700" />
              </>
            )}
          </div>
        </div>
      )}

      {/* ---- Create booking modal ---- */}
      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !creating && setCreateOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-3">{t('sched.create.title')}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-500">{t('sched.create.type')}</label>
                {cfgTypes.length > 0 ? (
                  <select className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
                    value={cForm.type} onChange={(e) => { setCForm((f) => ({ ...f, type: e.target.value })); setCSlots(null); setCChosen(null); }}>
                    <option value="">—</option>
                    {cfgTypes.map((tp) => <option key={tp.id} value={tp.id}>{tp.name || tp.id}</option>)}
                  </select>
                ) : (
                  <Input className="mt-1" value={cForm.type} onChange={(e) => { setCForm((f) => ({ ...f, type: e.target.value })); setCSlots(null); setCChosen(null); }} />
                )}
              </div>
              <div>
                {/* GOV-7 Issue B — profile-driven resource label (e.g. "Crew" for Movers); the underlying
                    provider/resource id field is unchanged. Selection is OPTIONAL (it only filters slots) — for
                    a Movers profile the legacy providers list is empty, so we show a free-text filter and never
                    inject a legacy provider. The booked provider comes from the chosen availability slot. */}
                <label className="text-xs text-gray-500" data-testid="create-resource-label">
                  {effResourceLabel ? cap(effResourceLabel) : t('sched.create.providerFilter')}
                </label>
                {/* §19/crew — three cases, all generic (no profile-name/industry branch):
                    (1) STAFF-oriented profile with configured providers → the provider <select> (calendar anchor).
                    (2) NON-person profile (e.g. Movers "equipment") with a tenant roster → an OPTIONAL crew
                        multi-select over the CANONICAL staff roster (names only; ids internal), assigned via the
                        canonical crew authority after create. Never lists a legacy provider, never free text.
                    (3) fallback (staff profile w/o configured providers, or empty roster) → the free-text filter. */}
                {cfgProviders.length > 0 && shouldShowProviderName(effResourceHint) ? (
                  <select className="mt-1 w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm"
                    value={cForm.provider} onChange={(e) => { setCForm((f) => ({ ...f, provider: e.target.value })); setCSlots(null); setCChosen(null); }}>
                    <option value="">—</option>
                    {cfgProviders.filter((p) => p.staff_id).map((p) => <option key={p.id} value={p.staff_id}>{p.name || p.staff_id}</option>)}
                  </select>
                ) : !shouldShowProviderName(effResourceHint) ? (
                  staffRoster.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-2" data-testid="create-crew-select">
                      {staffRoster.map((s) => (
                        <label key={s.id} data-testid="create-crew-option"
                          className={`inline-flex items-center gap-1 text-sm border rounded px-2 py-1 cursor-pointer ${createCrew.includes(s.id) ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                          <input type="checkbox" checked={createCrew.includes(s.id)} aria-label={s.name || s.email}
                            onChange={() => setCreateCrew((cur) => (cur.includes(s.id) ? cur.filter((x) => x !== s.id) : [...cur, s.id]))} />
                          {s.role ? `${s.name} (${cap(s.role)})` : s.name}
                        </label>
                      ))}
                    </div>
                  ) : (
                    /* Non-person profile with an EMPTY roster → truthful empty state, NEVER a free-text Crew
                       field. Booking stays creatable UNASSIGNED; crew is assigned later once staff exist. */
                    <p className="mt-1 text-[11px] text-gray-400" data-testid="create-crew-none">{t('sched.create.noCrew')}</p>
                  )
                ) : (
                  <Input className="mt-1" value={cForm.provider} onChange={(e) => { setCForm((f) => ({ ...f, provider: e.target.value })); setCSlots(null); setCChosen(null); }} />
                )}
              </div>

              <div>
                <Button variant="outline" size="sm" onClick={loadCreateSlots} disabled={cSlotsLoading}>
                  {cSlotsLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CalendarClock className="h-4 w-4 mr-1" />}
                  {t('sched.create.loadSlots')}
                </Button>
                {cSlots !== null && (
                  cSlotsLoading ? null : cSlots.length === 0 ? (
                    <p className="text-sm text-gray-500 mt-2">{t('sched.create.noSlots')}</p>
                  ) : (
                    <div className="mt-2 max-h-40 overflow-y-auto border border-gray-100 rounded-md divide-y divide-gray-100">
                      {cSlots.map((s, i) => {
                        const sel = cChosen && cChosen.start_ts === s.start_ts && cChosen.provider_staff_id === s.provider_staff_id;
                        return (
                          <button key={`${s.provider_staff_id}|${s.start_ts}|${i}`} data-testid="create-slot" onClick={() => setCChosen(s)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-purple-50 ${sel ? 'bg-purple-100' : ''}`}>
                            {fmt(s.start_ts)} – {fmt(s.end_ts)}{shouldShowProviderName(effResourceHint) && <> · {provName(s)}</>}
                          </button>
                        );
                      })}
                    </div>
                  )
                )}
              </div>

              {/* GOV-7 Issue B — customer NAME typeahead (no raw "Customer ID" free-text). Reuses the ONE
                  tenant-guarded customer search authority; selection sets customer_id from the result. */}
              <div className="relative">
                <label className="text-xs text-gray-500">{effCustomerLabel ? cap(effCustomerLabel) : t('sched.create.customer')}</label>
                <Input
                  className="mt-1"
                  data-testid="create-customer-search"
                  placeholder={t('sched.create.customerSearchPlaceholder')}
                  value={custQuery}
                  onChange={(e) => { setCustQuery(e.target.value); setCustSelected(null); setCForm((f) => ({ ...f, customerId: '' })); }}
                />
                {custSearching && <p className="text-[11px] text-gray-400 mt-1">{t('sched.create.searching')}</p>}
                {/* A FAILED SEARCH SAYS SO. Distinct from "no match" and distinct from loading, so
                    the operator knows to retry rather than concluding the patient does not exist. */}
                {custError && !custSearching && (
                  <p className="text-[11px] text-red-600 mt-1" data-testid="create-customer-search-error">
                    {t('sched.create.searchFailed')}
                  </p>
                )}
                {custResults !== null && !custSelected && !custSearching && !custError && (
                  custResults.length === 0 ? (
                    <p className="text-[11px] text-gray-400 mt-1">{t('sched.create.noCustomers')}</p>
                  ) : (
                    <div className="absolute z-10 left-0 right-0 mt-1 max-h-44 overflow-y-auto border border-gray-200 rounded-md bg-white shadow divide-y divide-gray-100">
                      {custResults.map((c) => (
                        <button
                          key={c.customer_id || c.id}
                          type="button"
                          data-testid="create-customer-result"
                          onClick={() => selectCustomer(c)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50"
                        >
                          <span className="font-medium text-gray-800">{c.name || t('sched.col.customer')}</span>
                          {safeContactLabel(c) && <span className="text-gray-400"> · {safeContactLabel(c)}</span>}
                        </button>
                      ))}
                    </div>
                  )
                )}
                {custSelected && (
                  <p className="text-[11px] text-green-700 mt-1" data-testid="create-customer-selected">
                    {custSelected.name}{safeContactLabel(custSelected) ? ` · ${safeContactLabel(custSelected)}` : ''}
                  </p>
                )}
              </div>
              {/* New-by-contact still supported (don't force selecting an existing CRM customer). */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div><label className="text-xs text-gray-500">{t('sched.create.phone')}</label><Input className="mt-1" data-testid="create-phone" value={cForm.phone} onChange={(e) => setCForm((f) => ({ ...f, phone: e.target.value }))} /></div>
                <div><label className="text-xs text-gray-500">{t('sched.create.email')}</label><Input className="mt-1" value={cForm.email} onChange={(e) => setCForm((f) => ({ ...f, email: e.target.value }))} /></div>
              </div>
              <p className="text-[11px] text-gray-400">{t('sched.create.newCustomerHint')}</p>

              {/* GOV-7 Issue B — profile-driven pickup/destination (shown ONLY when the effective booking_config
                  requires them). Sent as the existing canonical service_location / destination_location. */}
              {(pickupRequired || destinationRequired) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {pickupRequired && (
                    <div><label className="text-xs text-gray-500">{t('sched.create.pickup')}</label>
                      <Input className="mt-1" data-testid="create-pickup" value={cForm.pickup} onChange={(e) => setCForm((f) => ({ ...f, pickup: e.target.value }))} /></div>
                  )}
                  {destinationRequired && (
                    <div><label className="text-xs text-gray-500">{t('sched.create.destination')}</label>
                      <Input className="mt-1" data-testid="create-destination" value={cForm.destination} onChange={(e) => setCForm((f) => ({ ...f, destination: e.target.value }))} /></div>
                  )}
                </div>
              )}

              {/* GOV-7 Issue B — consent is displayed truthfully; there is NO staff self-grant control. */}
              {consentRequired && (
                <div className="text-[11px] text-amber-700 bg-amber-50 rounded-md px-2 py-1.5" data-testid="create-consent-required">
                  <span className="font-medium">{t('sched.create.consentRequired')}.</span> {t('sched.create.consentHint')}
                </div>
              )}

              <div><label className="text-xs text-gray-500">{t('sched.create.reason')}</label><Input className="mt-1" value={cForm.reason} onChange={(e) => setCForm((f) => ({ ...f, reason: e.target.value }))} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>{t('common.cancel')}</Button>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={doCreate} disabled={creating || !cChosen}>
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : t('sched.create.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Small presentational helpers
const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="text-gray-500 shrink-0">{label}</span>
    <span className="text-gray-900 text-right break-words">{value}</span>
  </div>
);

const ModalButtons = ({ t, acting, disabled, onCancel, onSubmit, submitLabel, submitClass }) => (
  <div className="flex justify-end gap-2">
    <Button variant="ghost" onClick={onCancel} disabled={acting}>{t('common.cancel')}</Button>
    <Button className={submitClass} onClick={onSubmit} disabled={acting || disabled}>
      {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
    </Button>
  </div>
);

const yesNo = (v, t) => (v ? t('common.yes') : t('common.no'));
const num = (v) => (v == null ? '—' : v);

export default SchedulingManager;
